// src/features/conversationSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { conversationsApi } from '~/api/conversations-api'; // The RTK query API service
import type { ConversationResponse } from '~/api/conversations-api';

// Define the Conversation type

// Define the initial state
interface ConversationState {
  conversation: ConversationResponse | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ConversationState = {
  conversation: null,
  status: 'idle',
  error: null,
};

// Create the slice
const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    applyTranslations: (state, action: { payload: { translations: { id: string; content: string }[]; transKey: string } }) => {
      const messages = (state.conversation as any)?.response?.messages;
      if (!Array.isArray(messages)) return;
      const { translations, transKey } = action.payload;
      (state.conversation as any).response.messages = messages.map((msg: any) => {
        const match = translations.find((t) => t.id === msg.id);
        return match ? { ...msg, [transKey]: match.content } : msg;
      });
    },
  },
  extraReducers: (builder) => {
    // Use RTK Query actions in the extraReducers
    builder
      .addMatcher(conversationsApi.endpoints.getConversationById.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(conversationsApi.endpoints.getConversationById.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.conversation = action.payload;
      })
      .addMatcher(conversationsApi.endpoints.getConversationById.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch conversation';
      })
      .addMatcher(conversationsApi.endpoints.updateConversation.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(conversationsApi.endpoints.updateConversation.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addMatcher(conversationsApi.endpoints.updateConversation.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update conversation';
      })
      .addMatcher(conversationsApi.endpoints.deleteConversation.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(conversationsApi.endpoints.deleteConversation.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.conversation?.response.id === action.meta.arg.originalArgs) {
          state.conversation = null;
        }
      })
      .addMatcher(conversationsApi.endpoints.deleteConversation.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to delete conversation';
      });
  },
});

// Export actions and reducer
export const { applyTranslations } = conversationSlice.actions;
export default conversationSlice.reducer;
